import React, { useState, useEffect } from "react";
import styles from "./InputWrapper.module.scss";
import tickImage from "../../assets/images/icon-tick-green.svg";
import { Label } from "../label/Label";
import { ErrorMessages } from "../error-messages/ErrorMessages";
import { Input, InputType, AutoCompleteType } from "../input/Input";
import { FieldModel, isValid } from "../../shared/model/field-model";
import { createElementName } from "../../shared/helpers/element.naming";

export type DisplayState = "normal" | "valid" | "invalid";

export interface InputEventArgs {
  currentValue: string;
  isValid: boolean;
  isTouched: boolean;
}

export interface InputWrapperProps {
  pageName: string;
  name: string;
  caption: string;
  fieldState: FieldModel;
  shouldDisplayRequired?: boolean;
  inputType?: InputType;
  errorMessageTitle?: string;
  autoCompleteType?: AutoCompleteType;
  onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onInputBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  readonly?: boolean;
}

export const InputWrapper = (props: InputWrapperProps) => {
  const [displayState, setDisplayState] = useState<DisplayState>("normal");
  useEffect(() => {
    if (props.fieldState.isTouched) {
      const state = isValid(props.fieldState) ? "valid" : "invalid";
      setDisplayState(() => state);
    }
  }, [props.fieldState.messages]);

  const inputElementName = createElementName(props.pageName, props.name, "input");
  const labelElementName = createElementName(props.pageName, props.name, "label");
  const messageElementName = createElementName(props.pageName, props.name, "error");

  return (
    <div className={styles.outer_container}>
      <div className={styles.label_container}>
        <Label
          name={labelElementName}
          htmlFor={inputElementName}
          displayValue={props.caption}
          shouldDisplayRequired={props.shouldDisplayRequired}
        />
      </div>
      <div className={styles.input_container}>
        <Input
          name={inputElementName}
          value={props.fieldState.value}
          inputType={props.inputType}
          autoCompleteType={props.autoCompleteType}
          onChange={props.onInputChange}
          onBlur={props.onInputBlur}
          showErrorBorder={props.fieldState.messages.length > 0}
          readonly={props.readonly}
        />
        {displayState === "valid" && <img className={styles.state_logo} src={tickImage} alt="Entry valid" />}
      </div>
      <div className={styles.errors_container}>
        <ErrorMessages name={messageElementName} messages={props.fieldState.messages} title={props.errorMessageTitle} />
      </div>
    </div>
  );
};
